import { createElement, setText } from './dom';
import { currentLanguage, setLabel } from './i18n';
import { createConversationSurface } from './conversationSurface';
import { createHistoricalSource } from './historicalSource';
import { townCall, townGreeting, townReplies, tradeLabel } from '../communication/dialogue';
import type { Townsperson } from '../communication/types';

class ConversationDialog {
  private readonly surface = createConversationSurface('town-dialog');
  private readonly reply = createElement('p', 'town-reply', this.surface.dialog);
  private readonly source = createHistoricalSource(this.surface.dialog);
  private readonly choices = createElement('div', 'conversation-choices', this.surface.dialog);
  private target: Townsperson | null = null;

  constructor(
    private readonly openChanged: (open: boolean) => void,
    private readonly spoken: (person: Townsperson, text: string) => void
  ) {
    setText(this.surface.close, 'Farewell');
    this.surface.close.addEventListener('click', () => {
      this.hide();
    });
    this.surface.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.hide();
    });
    setLabel(this.surface.dialog, 'aria-label', 'Conversation with a townsperson');
  }

  get person(): Townsperson | null {
    return this.target;
  }
  get isOpen(): boolean {
    return this.surface.dialog.open;
  }

  show(target: Townsperson, visitor: string): void {
    if (this.isOpen) return;
    this.target = target;
    const language = currentLanguage();
    this.surface.role.textContent = tradeLabel(target, language);
    this.surface.role.hidden = false;
    this.surface.title.textContent = target.name;
    const greeting = townGreeting(target, visitor, language);
    this.reply.textContent = greeting;
    this.source.set(null);
    this.choices.replaceChildren();
    for (const answer of townReplies(target, language)) {
      const button = createElement('button', 'conversation-choice', this.choices);
      button.type = 'button';
      button.textContent = answer.label;
      button.addEventListener('click', () => {
        this.reply.textContent = answer.text;
        this.source.set(answer.source);
        this.spoken(target, answer.bubble);
      });
    }
    this.openChanged(true);
    this.surface.dialog.showModal();
    this.choices.querySelector('button')?.focus();
    this.spoken(target, townCall(target, language));
  }

  hide(): void {
    if (!this.isOpen) return;
    this.surface.dialog.close();
    this.target = null;
    this.openChanged(false);
  }

  dispose(): void {
    this.hide();
    this.surface.dialog.remove();
  }
}

export function createConversationDialog(
  openChanged: (open: boolean) => void,
  spoken: (person: Townsperson, text: string) => void
) {
  return new ConversationDialog(openChanged, spoken);
}
